import { SubVariety, VarietalSummaryRow, Variety } from 'common/models';
import { Size } from 'common/models/growerBlock/size';
import {
  AddSubVarDto,
  EditSubVarDto,
} from 'features/settings-view/components/Sub-views/VarietiesView/utils/functions';
import { cmsApi } from '../cmsApi';
import { SubWithVarietyDto } from '../dto/get-subVarieties.dto';
import { EditRootstockDto, RootstockWithVarsDto } from '../dto/rootstock.dto';
import { shouldForceRefetch } from '../utils/handleExpiredSession';

const varietyBaseUrl = '/variety';
const subVarPath = '/sub-variety';
const rootstockPath = '/rootstock';

export const withVarietyEndpoints = cmsApi.injectEndpoints({
  endpoints: builder => ({
    getVarieties: builder.query<Variety[], void>({
      query: () => ({
        url: varietyBaseUrl,
      }),
      providesTags: (_, err) => (err ? [] : ['Varieties']),
      forceRefetch: shouldForceRefetch,
    }),

    getSubVarieties: builder.query<SubVariety[], void>({
      query: () => ({
        url: subVarPath,
      }),
      providesTags: (_, err) => (err ? [] : ['SubVarieties']),
      forceRefetch: shouldForceRefetch,
    }),

    getSubVarietiesWithVarieties: builder.query<SubWithVarietyDto[], void>({
      query: () => ({
        url: `${subVarPath}/with-varieties`,
      }),
      providesTags: (_, err) => (err ? [] : ['SubVarietiesWithVar']),
      forceRefetch: shouldForceRefetch,
    }),

    addSubVariety: builder.mutation<void, AddSubVarDto>({
      query: subVariety => ({
        url: subVarPath,
        method: 'POST',
        body: subVariety,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['SubVarietiesWithVar']),
    }),

    editSubVarieties: builder.mutation<void, EditSubVarDto[]>({
      query: subVarieties => ({
        url: subVarPath,
        method: 'PUT',
        body: subVarieties,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['SubVarietiesWithVar']),
    }),

    getRootstocksWithVarieties: builder.query<RootstockWithVarsDto[], void>({
      query: () => ({
        url: rootstockPath,
      }),
      providesTags: (_, err) => (err ? [] : ['RootstocksWithVar']),
      forceRefetch: shouldForceRefetch,
    }),

    addRootstock: builder.mutation<void, { code: string }>({
      query: rootstock => ({
        url: rootstockPath,
        method: 'POST',
        body: rootstock,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['RootstocksWithVar']),
    }),

    editRootstock: builder.mutation<void, EditRootstockDto>({
      query: rootstock => ({
        url: rootstockPath,
        method: 'PUT',
        body: rootstock,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['RootstocksWithVar']),
    }),

    getSizes: builder.query<Size[], void>({
      query: () => ({
        url: `${varietyBaseUrl}/sizes`,
      }),
      providesTags: (_, err) => (err ? [] : ['Sizes']),
      forceRefetch: shouldForceRefetch,
    }),

    getVarietalSummary: builder.query<
      VarietalSummaryRow[],
      { seasonId: number }
    >({
      query: ({ seasonId }) => ({
        url: `${varietyBaseUrl}/summary/${seasonId}`,
      }),
      // Always fetch the latest data available for the varietal summary.
      forceRefetch: () => true,
    }),
  }),
});

export const {
  useGetVarietiesQuery,
  useGetSubVarietiesQuery,
  useGetSubVarietiesWithVarietiesQuery,
  useAddSubVarietyMutation,
  useEditSubVarietiesMutation,
  useGetRootstocksWithVarietiesQuery,
  useAddRootstockMutation,
  useEditRootstockMutation,
  useGetSizesQuery,
  useGetVarietalSummaryQuery,
} = withVarietyEndpoints;
